/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component } from 'react';
import { DragLayer, DragLayerMonitor, XYCoord } from 'react-dnd';

import { Context, IWithContextProps, withContext } from '@tock/coretypes';
import { DEFAULT_WALK_IN_NAME } from 'utils/service/walkInUtils';
import { DndItemTypes } from 'constants/dndItemTypes';
import { ServiceAerialTable } from 'components/service/aerial/ServiceAerialTable';
import { ServicePartyDragMode } from 'state/service/ui';
import {
  guestListShowNotesSelector,
  guestListShowTagsSelector,
  isHdModeEnabledSelector,
} from 'selectors/dashboardPreferenceSelectors';
import { servicePartyDragModeSelector } from 'selectors/service/serviceUiSelector';
import BookingDragLayer from 'components/timeline/BookingDragLayer';
import InventorySquare from 'components/table-based-templates/InventorySquare';
import PartyRedesignCardWithMemoryMitigation from 'components/service/party/partyRedesignCard/PartyRedesignCardWithMemoryMitigation';
import SettingsAerialTable from 'components/settings/tables/SettingsAerialTable';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

const isTouchDevice = 'ontouchstart' in window;

function getItemStyles(props: Props) {
  const { currentOffset, item } = props;
  if (!currentOffset) {
    return {
      display: 'none',
    };
  }

  let { x, y } = currentOffset;

  if (item.extraOffset) {
    x += item.extraOffset.x;
    y += item.extraOffset.y;
  }

  const transform = `translate(${x}px, ${y}px)`;
  return {
    display: 'inline-block',
    transform,
    WebkitTransform: transform,
  };
}

interface ConnectProps extends DefaultConnectProps {
  dragMode: ServicePartyDragMode;
  isHdMode: boolean;
  showGuestListNotes: boolean;
  showGuestListTags: boolean;
}

interface Props {
  currentOffset: XYCoord | null;
  isDragging: boolean;
  item: any;
  itemType: string | symbol | null;
}

export function computePartyComponentRedesignVariant() {
  return PartyRedesignCardWithMemoryMitigation;
}

/**
 * a small utility for going from the feature status to the component to use for
 * PartyCards.
 */
export function computePartyComponent(_context: Context) {
  return computePartyComponentRedesignVariant();
}

class CustomDragLayer extends Component<Props & IWithContextProps & ConnectProps> {
  renderItem(type: string | symbol | null, item: any) {
    switch (type) {
      case DndItemTypes.PARTY: {
        const { party } = item;

        if (item.mode !== 'SERVICE') {
          return null;
        }

        let name = item.name ? item.name : (party.name ?? undefined);

        if (party?.type === 'WalkIn' && !name) {
          name = DEFAULT_WALK_IN_NAME;
        }

        return <BookingDragLayer name={name} />;
      }
      case DndItemTypes.SERVICE_AERIAL_TABLE: {
        return <ServiceAerialTable {...item} isDragPreview={true} />;
      }
      case DndItemTypes.SETTINGS_AERIAL_TABLE: {
        return <SettingsAerialTable {...item} isDragPreview={true} />;
      }
      case DndItemTypes.TEMPLATES_TEMPLATE_INFO: {
        const inventorySquareDetails = item.inventorySquareDetails;
        return (
          <InventorySquare
            colorClassName={inventorySquareDetails.colorClassName}
            displayColor={inventorySquareDetails.displayColor}
            text={inventorySquareDetails.text}
          />
        );
      }
      default: {
        if (item && item.renderedComponent && isTouchDevice) {
          return item.renderedComponent;
        } else {
          return null;
        }
      }
    }
  }

  render() {
    return (
      <div className="CustomDragLayer">
        {this.props.item && (
          <div style={getItemStyles(this.props)}>
            {this.renderItem(this.props.itemType, this.props.item)}
          </div>
        )}
      </div>
    );
  }
}

function collect(monitor: DragLayerMonitor) {
  return {
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
  };
}

export default connect<{}, ConnectProps>(
  DragLayer(collect)(withContext(CustomDragLayer)),
  (state: StoreType) => {
    return {
      dragMode: servicePartyDragModeSelector(state),
      isHdMode: isHdModeEnabledSelector(state),
      showGuestListNotes: guestListShowNotesSelector(state),
      showGuestListTags: guestListShowTagsSelector(state),
    };
  }
);
